import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaTelegramPlane, FaInstagram, FaVk } from 'react-icons/fa';
import { Mail, Phone } from 'lucide-react';
import api from '@/lib/api';
import { IDS } from '@/constants/testIds';

export default function Contacts() {
  const [s, setS] = useState({});
  useEffect(() => {
    api.get('/settings/public').then((r) => setS(r.data)).catch(() => {});
  }, []);

  const phone = (s.whatsapp_phone || '+918958956564');
  const phoneDisplay = phone.replace(/^(\+\d{2})(\d{4})(\d{3})(\d{3}).*$/, '$1 $2 $3 $4');
  const waLink = `https://wa.me/${phone.replace(/[^\d]/g, '')}?text=Здравствуйте! Интересует тур в Индию с гидом Али`;
  const tgLink = `https://t.me/${(s.telegram_username || phone).replace(/^\+/, '')}`;

  return (
    <section id="contacts" data-testid={IDS.sectionContacts} className="py-24 md:py-40 border-t border-border/60">
      <div className="container-x">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="mb-16 max-w-3xl"
        >
          <p className="overline mb-4">№ 07 · Get in touch</p>
          <h2 className="font-serif-display text-5xl md:text-6xl lg:text-8xl leading-[0.9]">
            Готовы<br />
            <em className="text-saffron">поехать?</em>
          </h2>
          <p className="text-foreground/70 mt-6 max-w-lg text-base md:text-lg font-light">
            Пишите напрямую Али в WhatsApp или Telegram — ответ обычно в течение часа (по индийскому времени +05:30).
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <motion.a
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            whileHover={{ y: -4 }}
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative overflow-hidden border border-border p-8 md:p-10 bg-white hover:border-[#25D366] transition-colors"
          >
            <div className="flex items-start justify-between mb-8">
              <FaWhatsapp className="text-4xl text-[#25D366]" />
              <span className="text-[10px] uppercase tracking-widest2 text-foreground/50">Preferred</span>
            </div>
            <h3 className="font-serif-display text-3xl md:text-4xl mb-2">WhatsApp</h3>
            <p className="text-foreground/60 text-sm md:text-base mb-4">{phoneDisplay}</p>
            <div className="text-xs uppercase tracking-widest2 font-bold text-[#25D366] flex items-center gap-2 mt-6">
              Написать сейчас
              <span className="w-6 h-px bg-[#25D366] transition-all group-hover:w-12" />
            </div>
          </motion.a>

          <motion.a
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.08 }}
            whileHover={{ y: -4 }}
            href={tgLink}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative overflow-hidden border border-border p-8 md:p-10 bg-white hover:border-[#26A5E4] transition-colors"
          >
            <div className="flex items-start justify-between mb-8">
              <FaTelegramPlane className="text-4xl text-[#26A5E4]" />
              <span className="text-[10px] uppercase tracking-widest2 text-foreground/50">Instant</span>
            </div>
            <h3 className="font-serif-display text-3xl md:text-4xl mb-2">Telegram</h3>
            <p className="text-foreground/60 text-sm md:text-base mb-4">{phoneDisplay}</p>
            <div className="text-xs uppercase tracking-widest2 font-bold text-[#26A5E4] flex items-center gap-2 mt-6">
              Написать сейчас
              <span className="w-6 h-px bg-[#26A5E4] transition-all group-hover:w-12" />
            </div>
          </motion.a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
          <a href={`mailto:${s.email || 'haiderali89589@gmail.com'}`} className="border border-border p-6 hover:border-saffron transition-colors group">
            <Mail className="text-saffron mb-3" size={22} />
            <div className="text-xs uppercase tracking-widest2 text-foreground/50 mb-1">Email</div>
            <div className="text-sm text-foreground/90 group-hover:text-saffron transition-colors truncate">{s.email || 'haiderali89589@gmail.com'}</div>
          </a>
          <a href={`tel:${phone}`} className="border border-border p-6 hover:border-saffron transition-colors group">
            <Phone className="text-saffron mb-3" size={22} />
            <div className="text-xs uppercase tracking-widest2 text-foreground/50 mb-1">Max / Звонок</div>
            <div className="text-sm text-foreground/90 group-hover:text-saffron transition-colors">{phoneDisplay}</div>
          </a>
          {s.instagram_url && (
            <a href={s.instagram_url} target="_blank" rel="noopener noreferrer" className="border border-border p-6 hover:border-saffron transition-colors group">
              <FaInstagram className="text-saffron mb-3" size={22} />
              <div className="text-xs uppercase tracking-widest2 text-foreground/50 mb-1">Instagram</div>
              <div className="text-sm text-foreground/90 group-hover:text-saffron transition-colors truncate">{s.instagram_handle || '@privateindiaguidedtourss'}</div>
            </a>
          )}
          {s.vk_url && (
            <a href={s.vk_url} target="_blank" rel="noopener noreferrer" className="border border-border p-6 hover:border-saffron transition-colors group">
              <FaVk className="text-saffron mb-3" size={22} />
              <div className="text-xs uppercase tracking-widest2 text-foreground/50 mb-1">VK</div>
              <div className="text-sm text-foreground/90 group-hover:text-saffron transition-colors">privateindiaguideali</div>
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
